"use client";

import { useAbility } from "./useAbility";

export interface ThreadPermissions {
  canViewThreads: boolean;
  canCreateThreads: boolean;
  canDeleteThreads: boolean;
  canEditThreads: boolean;
  /**
   * True if the principal holds a read THREAD grant at ANY scope, not just `scopeId` —
   * i.e. the Project Assist surface is relevant to them at all. Mirrors the backend's
   * GET /threads admit check (any grant, then scope-filtered list): a role assigned below
   * the current scope (e.g. PROJECT) still grants this, even though `canViewThreads` at
   * the ancestor (e.g. TEAM) scope would be false. Never grants visibility into threads
   * beyond what the API returns (own threads, readable scopes only).
   */
  canUseProjectAssist: boolean;
  /**
   * True if the principal holds a create THREAD grant at ANY scope. Gates
   * new-chat affordances where the thread's future anchor is whatever
   * project/document the user picks next (standalone /chat landing).
   */
  canCreateThreadsAnywhere: boolean;
  isLoading: boolean;
}

/** Scope-level Project Assist permissions (CASL subject THREAD) at a chat scope. */
export function useThreadPermissions(scopeId: string | undefined): ThreadPermissions {
  const { can, isLoading } = useAbility();

  return {
    canViewThreads: !!scopeId && can("read", { scopeType: "THREAD", scopeId }),
    canCreateThreads: !!scopeId && can("create", { scopeType: "THREAD", scopeId }),
    canDeleteThreads: !!scopeId && can("delete", { scopeType: "THREAD", scopeId }),
    canEditThreads: !!scopeId && can("update", { scopeType: "THREAD", scopeId }),
    canUseProjectAssist: can("read", { scopeType: "THREAD" }),
    canCreateThreadsAnywhere: can("create", { scopeType: "THREAD" }),
    isLoading,
  };
}
